import * as React from 'react';
import { ReactNode, useContext, useEffect, createRef, useState } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { AuthContext } from '../Auth/components/AuthProvider';
import { getMandantSettingsByUsername } from '../util/mandant-settings-utils';
import { Menubar } from 'primereact/menubar';
import { LanguagePicker } from '../Intl/language-picker';
import { Button } from 'primereact/button';
import { StateContext, StateContextType } from '../util/providers/StateProvider';
import { Menu } from 'primereact/menu';
import { MenuItem, MenuItemCommandEvent } from 'primereact/menuitem';
import { TieredMenu } from 'primereact/tieredmenu';
import { Link, useNavigate } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBookOpen,
  faFloppyDisk,
  faInfoCircle,
  faLandmark,
  faMagnifyingGlass,
  faPlus,
  faSquarePlus,
  faUser,
  faUsers,
} from '@fortawesome/free-solid-svg-icons';
import { requireImageAsync } from '../util/require-image';
import { PageData } from './homepage/types';
import { AuthContextType } from '../Auth/utils/interfaces';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { EmbeddedSearchbar } from './components/EmbeddedSearchbar';
import { UserAvatarComponent } from './components/UserAvatar';

export const icon = (main: IconProp, addon?: IconProp): ReactNode => {
  return (
    <span className="fa-layers fa-1x w-2rem mr-2">
      <FontAwesomeIcon icon={main}/>
      {addon && <FontAwesomeIcon icon={addon} style={{marginTop: '-0.7em', marginRight: '-0.2em', fontSize: '0.5em'}}/>}
    </span>
  );
};

export const Header = (): ReactNode => {
  const intl: IntlShape = useIntl();
  const stateContext: StateContextType = useContext(StateContext);
  const authContext: AuthContextType = useContext(AuthContext);
  const {authorizer, session} = authContext;
  const [mandantLogo, setMandantLogo] = useState<string | null>();
  const navigate = useNavigate();
  const debugMenuRef = createRef<TieredMenu>();
  const userMenuRef = createRef<Menu>();
  const pageData: PageData = stateContext.pageData;
  const [debugMenuItems, setDebugMenuItems] = useState<MenuItem[]>(undefined);

  useEffect((): void => {
    void requireImageAsync(`Settings/mandants/${stateContext.originMandantSettings.mandant}/${pageData.header.logo.svg}`)
      .then((result: string): void => setMandantLogo(result));
  }, [stateContext]);

  useEffect((): void => {
    if(process.env.REACT_APP_PRODUCTION === 'false') {
      void import('./components/debug/debug-menu').then(({getDebugMenu}): void => {
        setDebugMenuItems(_ => getDebugMenu(stateContext, authContext));
      });
    }
  }, [stateContext, authContext]);

  const menuItems: MenuItem[] = [
    {
      icon: 'pi pi-home',
      label: intl.formatMessage({id: 'layout.header.menu.homepage'}),
      url: '/',
      command: (event: MenuItemCommandEvent): void => {
        event.originalEvent.preventDefault();
        void navigate('/');
      },
    },
    {
      icon: 'pi pi-list',
      label: intl.formatMessage({id: 'layout.header.menu.publistGenerator'}),
      className: 'w-25',
      items: [
        {
          icon: icon(faSquarePlus),
          label: intl.formatMessage({id: 'layout.header.menu.publist.generator'}),
          url: '/publist/generator/publications',
          command: (event: MenuItemCommandEvent): void => {
            event.originalEvent.preventDefault();
            void navigate('/publist/generator/publications');
          },
        },
        {
          icon: icon(faSquarePlus),
          label: intl.formatMessage({id: 'layout.header.menu.research.generator'}),
          url: '/publist/generator/research',
          command: (event: MenuItemCommandEvent): void => {
            event.originalEvent.preventDefault();
            void navigate('/publist/generator/research');
          },
        },
        ...authorizer.isNotAnonymous() ? [{
          icon: icon(faFloppyDisk),
          label: intl.formatMessage({id: 'layout.header.menu.publist.userPublications'}),
          url: '/user/profile/publists',
          command: (event: MenuItemCommandEvent): void => {
            event.originalEvent.preventDefault();
            void navigate('/user/profile/publists');
          },
        }] : [],
        {
          icon: icon(faInfoCircle),
          label: intl.formatMessage({id: 'layout.header.menu.publist.documentation'}),
          url: '/publist/documentation',
          command: (event: MenuItemCommandEvent): void => {
            event.originalEvent.preventDefault();
            void navigate('/publist/documentation');
          },
        },
      ],
    },
    {
      icon: 'pi pi-search',
      label: intl.formatMessage({id: 'layout.header.menu.search'}),
      items: [
        {
          icon: icon(faBookOpen, faMagnifyingGlass),
          label: intl.formatMessage({id: 'entity.work'}),
          url: '/search/works',
          command: (event: MenuItemCommandEvent): void => {
            event.originalEvent.preventDefault();
            void navigate('/search/works');
          },
        },
        {
          icon: icon(faUser, faMagnifyingGlass),
          label: intl.formatMessage({id: 'entity.person'}),
          url: '/search/persons',
          command: (event: MenuItemCommandEvent): void => {
            event.originalEvent.preventDefault();
            void navigate('/search/persons');
          },
        },
        {
          icon: icon(faLandmark, faMagnifyingGlass),
          label: intl.formatMessage({id: 'entity.organisation'}),
          url: '/search/organisations',
          command: (event: MenuItemCommandEvent): void => {
            event.originalEvent.preventDefault();
            void navigate('/search/organisations');
          },
        },
        {
          icon: icon(faUsers, faMagnifyingGlass),
          label: intl.formatMessage({id: 'entity.project'}),
          url: '/search/projects',
          command: (event: MenuItemCommandEvent): void => {
            event.originalEvent.preventDefault();
            void navigate('/search/projects');
          },
        },
      ],
    },
    ...authorizer.hasAnyOfRoles(['ROLE_USER', 'ROLE_EDITOR', 'ROLE_ADMIN']) ? [{
      icon: 'pi pi-file-plus',
      label: intl.formatMessage({id: 'layout.header.menu.create'}),
      items: [
        {
          icon: icon(faBookOpen, faPlus),
          label: intl.formatMessage({id: 'entity.work'}),
          url: '/work/add',
          command: (event: MenuItemCommandEvent): void => {
            event.originalEvent.preventDefault();
            void navigate('/work/add');
          },
        },
        ...authorizer.hasAnyOfRoles(['ROLE_EDITOR', 'ROLE_ADMIN']) ? [
          {
            icon: icon(faUser, faPlus),
            label: intl.formatMessage({id: 'entity.person'}),
            url: '/person/add',
            command: (event: MenuItemCommandEvent): void => {
              event.originalEvent.preventDefault();
              void navigate('/person/add');
            },
          },
          {
            icon: icon(faLandmark, faPlus),
            label: intl.formatMessage({id: 'entity.organisation'}),
            url: '/organisation/add',
            command: (event: MenuItemCommandEvent): void => {
              event.originalEvent.preventDefault();
              void navigate('/organisation/add');
            },
          },
        ] : [],
        {
          icon: icon(faUsers, faPlus),
          label: intl.formatMessage({id: 'entity.project'}),
          url: '/project/add',
          command: (event: MenuItemCommandEvent): void => {
            event.originalEvent.preventDefault();
            void navigate('/project/add');
          },
        },
      ],
    }] : [],
  ];


  const userMenuItems: MenuItem[] = [
    {
      icon: 'pi pi-user',
      label: intl.formatMessage({ id: 'userMenu.profile' }),
      url: '/user/profile',
      command: (event: MenuItemCommandEvent): void => {
        event.originalEvent.preventDefault();
        void navigate('/user/profile');
      },
    },
    ...(authorizer.hasRole('ROLE_ADMIN')
      ? [
          {
            icon: 'pi pi-users',
            label: intl.formatMessage({ id: 'userMenu.userManagement' }),
            url: '/admin/users',
            command: (event: MenuItemCommandEvent): void => {
              event.originalEvent.preventDefault();
              void navigate('/admin/users');
            },
          },
        /*
          {
            icon: 'pi pi-calculator',
            label: 'MathJax Test',
            url: '/admin/mathjax-test',
            command: (event: MenuItemCommandEvent): void => {
              event.originalEvent.preventDefault();
              void navigate('/admin/mathjax-test');
            },
          },*/
        ]
      : []),
    ...(authorizer.hasRoleOrHigher('ROLE_EDITOR')
      ? [
          {
            icon: 'pi pi-server',
            label: intl.formatMessage({ id: 'userMenu.adminSystem' }),
            url: '/admin/system',
            command: (event: MenuItemCommandEvent): void => {
              event.originalEvent.preventDefault();
              void navigate('/admin/system');
            },
          }
        ]
      : []),
    {
      icon: 'pi pi-cog',
      label: intl.formatMessage({ id: 'userMenu.settings' }),
      url: '/user/settings/profile',
      command: (event: MenuItemCommandEvent): void => {
        event.originalEvent.preventDefault();
        void navigate('/user/settings/profile');
      },
    },
    {
      icon: 'pi pi-sign-out',
      label: intl.formatMessage({ id: 'userMenu.logout' }),
      url: '/logout',
      command: (event: MenuItemCommandEvent): void => {
        event.originalEvent.preventDefault();
        void navigate('/logout');
      },
    },
  ];


  const endTop: ReactNode = <div className={'menu-end flex align-items-center gap-2 justify-content-end'}>
    <div className={'inline-block'}><LanguagePicker single={true}/></div>
    {process.env.REACT_APP_PRODUCTION === 'false' && process.env.REACT_APP_DEMO_LOGIN_ENABLED === 'true' &&
      <>
        <TieredMenu model={debugMenuItems} popup ref={debugMenuRef} id="popup_debug_menu"/>
        <Button
          icon={'pi pi-cog'}
          text={true}
          rounded={true}
          onClick={(event): void => debugMenuRef.current.toggle(event)}
        />
      </>
    }
    <Menu model={userMenuItems} popup ref={userMenuRef} id="popup_user_menu"/>
    {authorizer.isNotAnonymous() && <Button
      text={true}
      rounded={true}
      pt={{root: {className: 'p-0'}}}
      onClick={(event): void => userMenuRef.current.toggle(event)}
    >
      <UserAvatarComponent currentUser={true} type={'small'} />
    </Button>}
    {authorizer.isAnonymous() && stateContext.originMandantSettings.loginEnabled &&
      <Button
        text={true}
        rounded={true}
        tooltip={intl.formatMessage({id: 'layout.header.menu.login'})}
        tooltipOptions={{ position: 'bottom' }}
        icon={"pi pi-sign-in"}
        pt={{root: {className: 'p-0'}}}
        onClick={(event): void => {
          event.preventDefault();
          void navigate('/login');
        }}
      />
      }
  </div>;

  return <header className={'p-3 pb-0 flex flex-column gap-1 surface-100 shadowed z-1'}>
    <div className={'w-full flex justify-content-between h-3rem'}>
      <div className={'w-25rem flex h-full'}>
        <Link className={'block w-full h-full max-w-15rem'} to={pageData.header.logo.link}>
          <img className={'w-full h-full'}
               src={mandantLogo}
               alt={pageData.header.logo.alt}/>
        </Link>
      </div>
      <div className={'hidden xl:block justify-content-center'}>
        <a href={'/'} className={'no-underline'}>
          <h1 className={'m-0'}>{intl.formatMessage({id: 'layout.header.title'})}</h1>
        </a>
      </div>
      <div className={'w-25rem block justify-content-end'}>
        {endTop}
      </div>
    </div>
    <div className={'block xl:hidden flex justify-content-center'}>
      <a href={'/'} className={'no-underline'}>
        <h1 className={'m-0'} style={{textAlign: 'center', fontSize: 'max(min(calc(10px + 3.5vw), 2rem), 22px)'}}>{intl.formatMessage({id: 'layout.header.title'})}</h1>
      </a>
    </div>
    <div className={'w-full flex flex-row gap-2 mb-2'}>
      <div className={'flex'}>
        <Menubar
          pt={{root: {className: 'border-noround border-none m-0 p-0 bg-transparent'}, menu: {className: 'lg:flex lg:flex-nowrap'}}}
          model={menuItems}/>
      </div>
      <div className={'flex flex-grow-1 justify-content-end'}>
        <div id={'header-searchbar-container'} className={'flex w-12'}>
          <EmbeddedSearchbar/>
        </div>
      </div>
    </div>
  </header>;
};

export default Header;
